import { h } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { fmtWeight, fmtDurationLong } from "../lib/format.js";
import { icon } from "../lib/icons.js";
import { muscleByName } from "../store.js";
import { exerciseThumb } from "./common.js";
import { MuscleMap } from "./MuscleMap.js";
import { editNextSession, editWeeklyPlan } from "./trainingPlan.js";

export function comparisonDelta(c) {
  if (c.metric === "time") {
    // Faster time is an improvement
    if (c.change < 0) return { text: `−${fmtDurationLong(Math.abs(c.change))}`, class: "delta-up", isImprovement: true };
    if (c.change > 0) return { text: `+${fmtDurationLong(c.change)}`, class: "delta-down", isImprovement: false };
    return { text: "= same", class: "delta-flat", isImprovement: false };
  }
  if (c.change > 0) {
    const val = c.metric === "e1rm" ? `+${fmtWeight(c.change)}` : `+${c.change} reps`;
    return { text: val, class: "delta-up", isImprovement: true };
  }
  if (c.change < 0) {
    const val = c.metric === "e1rm" ? `−${fmtWeight(Math.abs(c.change))}` : `−${Math.abs(c.change)} reps`;
    return { text: val, class: "delta-down", isImprovement: false };
  }
  return { text: "= same", class: "delta-flat", isImprovement: false };
}

export function comparisonText(c) {
  const value = n => c.metric === "e1rm" ? fmtWeight(n) : c.metric === "time" ? fmtDurationLong(n) : `${n} reps`;
  const context = c.metric === "e1rm" ? "e1RM" : c.metric === "time" ? `${(c.distance_m/1000).toLocaleString()} km` : `at ${fmtWeight(c.bodyweight_kg)}`;
  const delta = comparisonDelta(c);
  return `${c.exercise_name}: ${value(c.current)} (${delta.text}) vs ${value(c.baseline)} prev · ${context}`;
}

export function reviewObservation(review) {
  if (!review) return "Your weekly review brings your training together.";
  const relevant = (review.comparisons || []).filter(c => review.training_goal === "endurance" ? c.metric === "time" : review.training_goal === "strength" || review.training_goal === "hypertrophy" ? c.metric !== "time" : true);

  // Look for the best improvement first:
  const improvements = relevant.filter(c => (c.metric === "time" ? c.change < 0 : c.change > 0));
  if (improvements.length) {
    const best = improvements.sort((a, b) => {
      const deltaA = a.metric === "time" ? Math.abs(a.change) : a.change;
      const deltaB = b.metric === "time" ? Math.abs(b.change) : b.change;
      return deltaB - deltaA;
    })[0];
    const delta = comparisonDelta(best);
    const tag = best.metric === "e1rm" ? "e1RM" : best.metric === "time" ? "time" : "reps";
    return `${best.exercise_name} up ${delta.text} ${tag} vs recent best.`;
  }

  // If no improvement, but exercises matched previous best:
  const matched = relevant.filter(c => c.change === 0);
  if (matched.length) {
    return `Matched recent personal bests on ${matched.length} exercise${matched.length === 1 ? "" : "s"}.`;
  }

  // If there are comparisons but all slightly below baseline:
  if (relevant.length) {
    return `${relevant.length} exercise comparison${relevant.length === 1 ? "" : "s"} ready in this week's review.`;
  }

  if (review.paused) return "This week's goal is paused · continuity preserved.";
  if (review.workouts >= review.goal && review.workouts > 0) {
    return `Weekly goal achieved! ${review.workouts} of ${review.goal} planned workouts completed (${review.working_sets} working sets).`;
  }
  if (review.workouts > 0) {
    return `${review.workouts} of ${review.goal} planned workouts logged (${review.working_sets} working sets).`;
  }
  return `${review.goal} workouts planned for this week.`;
}

export function weeklyReviewCard(review, { compact = false, onSaved = () => {} } = {}) {
  if (!review) return null;
  if (compact) {
    const relevant = (review.comparisons || []).filter(c => review.training_goal === "endurance" ? c.metric === "time" : review.training_goal === "strength" || review.training_goal === "hypertrophy" ? c.metric !== "time" : true);
    const improvements = relevant.filter(c => (c.metric === "time" ? c.change < 0 : c.change > 0));
    const featured = improvements.length
      ? improvements.sort((a, b) => (b.metric === "time" ? Math.abs(b.change) : b.change) - (a.metric === "time" ? Math.abs(a.change) : a.change))[0]
      : relevant[0];

    return h("button", {
      class: "card card-tap row-between review-observation-card",
      onClick: () => navigate(`/week/${review.week_start}`)
    },
      h("div", { class: "hstack", style: { gap: "12px", alignItems: "center", minWidth: 0, flex: "1" } },
        featured?.images?.length
          ? exerciseThumb(featured.images, { size: 42 })
          : h("div", { class: "review-thumb-icon" }, icon("chart", { size: 20, className: "icon accent" })),
        h("div", { class: "grow", style: { textAlign: "left", minWidth: 0 } },
          h("span", { class: "eyebrow" }, "THIS WEEK'S REVIEW"),
          h("div", { class: "review-headline" }, reviewObservation(review)),
          h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "2px" } }, "View comparisons & muscle map →")
        )
      ),
      icon("forward", { size: 18, className: "icon muted" })
    );
  }

  const focus = review.training_goal;
  const comparisons = (review.comparisons || []).filter(c => focus === "endurance" ? c.metric === "time" : focus === "strength" || focus === "hypertrophy" ? c.metric !== "time" : true);

  const subText = review.paused
    ? "Continuity preserved. Logged workouts remain in your history."
    : review.workouts >= review.goal
      ? "Goal achieved! Solid consistency on your training plan."
      : review.workouts > 0
        ? `${review.workouts} completed. ${review.goal - review.workouts} more planned to reach your goal.`
        : "No workouts logged yet this week.";

  const formatCompVal = (c, val) => c.metric === "e1rm" ? fmtWeight(val) : c.metric === "time" ? fmtDurationLong(val) : `${val} reps`;
  const formatCompTag = (c) => c.metric === "e1rm" ? "e1RM" : c.metric === "time" ? `${(c.distance_m/1000).toLocaleString()} km` : "reps";

  return h("div", { class: "weekly-review" },
    h("section", { class: "review-summary" },
      h("span", { class: "eyebrow" }, review.in_progress ? "WEEK IN PROGRESS" : "WEEK COMPLETE"),
      h("h2", null, review.paused ? "A paused goal week" : `${review.workouts} of ${review.goal} planned workouts`),
      h("p", { class: "review-sub" }, subText),
      h("div", { class: "review-stat-grid" },
        h("div", { class: "review-stat-tile" },
          h("span", { class: "num" }, String(review.working_sets)),
          h("span", { class: "lbl" }, "working sets")
        ),
        h("div", { class: "review-stat-tile" },
          h("span", { class: "num" }, fmtDurationLong(review.duration_seconds || 0)),
          h("span", { class: "lbl" }, "session time")
        ),
        (focus === "hypertrophy" || focus === "strength") ? h("div", { class: "review-stat-tile" },
          h("span", { class: "num" }, String(review.hard_sets)),
          h("span", { class: "lbl" }, "hard sets (RPE ≥7)")
        ) : null,
        review.distance_m > 0 ? h("div", { class: "review-stat-tile" },
          h("span", { class: "num" }, `${(review.distance_m/1000).toLocaleString()} km`),
          h("span", { class: "lbl" }, "distance logged")
        ) : null
      )
    ),

    // Muscle coverage body map
    (review.primary_muscle_ids?.length || review.secondary_muscle_ids?.length)
      ? h("section", { class: "review-section" },
          h("h2", null, "Muscle coverage this week"),
          h("p", { class: "dim", style: { fontSize: ".84rem", marginBottom: "12px" } }, "Muscles targeted across your completed sessions this week. Tap any region to inspect."),
          h("div", { class: "card review-muscle-card" },
            MuscleMap({
              primary: review.primary_muscle_ids || [],
              secondary: review.secondary_muscle_ids || [],
              onRegionClick: (name) => {
                const m = muscleByName(name);
                if (m) navigate(`/muscle/${m.muscle_id}`);
              }
            }),
            review.muscles_trained?.length
              ? h("div", { class: "review-muscle-list" },
                  ...review.muscles_trained.map(m => h("button", {
                    class: "review-muscle-chip",
                    onClick: () => navigate(`/muscle/${m.muscle_id}`)
                  },
                    h("span", { class: "chip-name" }, m.muscle),
                    h("span", { class: "chip-sets" }, `${m.sets} sets`)
                  ))
                )
              : null
          )
        )
      : null,

    h("section", { class: "review-section" },
      h("h2", null, "Comparable performances"),
      h("p", { class: "dim", style: { fontSize: ".84rem", marginBottom: "14px" } }, `Compared against your best performance in the prior 4 weeks (${review.baseline_start} to ${review.week_start}).`),
      comparisons.length
        ? h("div", { class: "comparisons-list" },
            ...comparisons.map(c => {
              const delta = comparisonDelta(c);
              return h("button", {
                class: "comparison-card card-tap",
                onClick: () => navigate(`/exercise/${c.exercise_id}`)
              },
                exerciseThumb(c.images, { size: 44 }),
                h("div", { class: "comp-info" },
                  h("strong", { class: "comp-title" }, c.exercise_name),
                  h("div", { class: "comp-meta" },
                    h("span", null, formatCompVal(c, c.current)),
                    h("span", { class: "comp-sep" }, "vs"),
                    h("span", null, `${formatCompVal(c, c.baseline)} prev`),
                    h("span", { class: "comp-tag" }, formatCompTag(c))
                  )
                ),
                h("div", { class: "comp-right" },
                  h("span", { class: ["comp-delta", delta.class] }, delta.text),
                  icon("forward", { size: 16, className: "icon muted" })
                )
              );
            }),
            h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "12px", lineHeight: "1.4" } },
              "💡 Comparisons match like-for-like exercises and bodyweights. Tap any exercise to view your full history."
            )
          )
        : h("p", { class: "empty-note" }, "Not enough comparable data yet. Comparisons appear once an exercise has matching data logged across multiple weeks.")
    ),

    h("section", { class: "review-section" },
      h("h2", null, "Sessions this week"),
      review.sessions.length
        ? h("div", null,
            ...review.sessions.map(s => {
              const d = new Date(s.started_at.includes("T") ? s.started_at : s.started_at.replace(" ", "T") + "Z");
              const dateFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(d);
              return h("button", {
                class: "review-session-card card-tap",
                onClick: () => navigate(`/history/session/${s.id}`)
              },
                h("div", { class: "grow", style: { minWidth: 0 } },
                  h("strong", { style: { fontSize: ".92rem", display: "block", color: "var(--text)" } }, s.name || "Workout"),
                  h("div", { class: "dim", style: { fontSize: ".8rem", marginTop: "2px" } },
                    `${dateFmt} · ${s.working_sets} working sets · ${fmtDurationLong(s.duration_seconds || 0)}`
                  )
                ),
                icon("forward", { size: 16, className: "icon muted" })
              );
            })
          )
        : h("p", { class: "empty-note" }, "No completed workouts logged in this week.")
    ),

    h("div", { class: "btn-row mt-lg" },
      h("button", { class: "btn btn-primary grow", onClick: () => editNextSession(onSaved) }, icon("plus", { size: 16 }), "Plan my next session"),
      h("button", { class: "btn btn-ghost grow", onClick: () => editWeeklyPlan(onSaved) }, icon("list", { size: 16 }), "Adjust weekly plan")
    )
  );
}
